import React, { useEffect, useState, MouseEvent } from 'react';
import { useApolloClient } from '@apollo/client';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  createBookmarkMutation,
  deleteBookmarkMutation
} from '../../api/mutations';
import Bugsnag from '../../utils/bugsnag';
import {
  addBookmarkToCache,
  removeBookmarkFromCache
} from '../../utils/helpers/updateBookmarkCache';
import FavoriteIcon from '../../public/static/images/item/favorite.svg';
import FavoriteFilledIcon from '../../public/static/images/item/filled-favorite.svg';
import { Icon } from '../Icon';
import { BookmarkArea, BookmarkContainer } from './styled';
import { setError } from '../../actions';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const pendingItemIds = new Set<string>();
const queuedStateByItemId = new Map<string, boolean>();

interface BookmarkProps {
  itemId: string;
  isActive?: boolean;
  isCurrentItemPage?: boolean;
  shouldUpdateCache?: boolean;
  removeItemFunc?: () => void;
  addBookmark?: () => void;
  deleteBookmark?: () => void;
  additionalClasses?: string;
  isGalleryHeader?: boolean;
  setFavorited?: (val: boolean) => void;
  stat?: string;
  isWebView?: boolean;
  renderCurrentIcons?: boolean;
  setErrorUI: (value: boolean) => void;
}

const BookmarkComponent: React.FC<BookmarkProps> = ({
  itemId,
  isActive = false,
  isCurrentItemPage = false,
  shouldUpdateCache = false,
  additionalClasses,
  isGalleryHeader = false,
  setFavorited,
  stat,
  isWebView = false,
  renderCurrentIcons = false,
  setErrorUI
}) => {
  const client = useApolloClient();
  const [active, setActive] = useState(isActive);

  useEffect(() => {
    setActive(isActive);
  }, [isActive]);

  const onMutationFailed = (error: unknown) => {
    setErrorUI(true);
    Bugsnag.notify(error as Error);
  };

  const addBookmark = async (id: string): Promise<boolean> => {
    try {
      const { data } = await client.mutate({
        variables: { itemId: id },
        mutation: createBookmarkMutation
      });

      if (data?.createBookmark?.errors.length) {
        onMutationFailed(
          new Error(
            data.createBookmark.errors.map(({ message }) => message).join('; ')
          )
        );
        return false;
      }

      if (shouldUpdateCache) addBookmarkToCache(id, client);
      return true;
    } catch (error) {
      onMutationFailed(error);
      return false;
    }
  };

  const deleteBookmark = async (id: string): Promise<boolean> => {
    try {
      const { data } = await client.mutate({
        variables: { itemId: id },
        mutation: deleteBookmarkMutation
      });

      if (data?.deleteBookmark?.errors.length) {
        onMutationFailed(
          new Error(
            data.deleteBookmark.errors.map(({ message }) => message).join('; ')
          )
        );
        return false;
      }

      if (shouldUpdateCache) removeBookmarkFromCache(id, client);
      return true;
    } catch (error) {
      onMutationFailed(error);
      return false;
    }
  };

  const runToggle = async (id: string, desiredState: boolean) => {
    pendingItemIds.add(id);

    const succeeded = desiredState
      ? await addBookmark(id)
      : await deleteBookmark(id);

    pendingItemIds.delete(id);

    const queuedState = queuedStateByItemId.get(id);
    if (queuedState !== undefined) {
      queuedStateByItemId.delete(id);
      if (!succeeded || queuedState !== desiredState) {
        runToggle(id, queuedState);
      }
    }
  };

  const toggleBookmark = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const nextState = !active;

    if (setFavorited) {
      setFavorited(nextState);
    }

    setActive(nextState);

    if (pendingItemIds.has(itemId)) {
      queuedStateByItemId.set(itemId, nextState);
      return;
    }

    runToggle(itemId, nextState);
  };

  const itemIdValue = itemId ? ` ${itemId}` : '';
  const ariaLabel = active
    ? `Remove ad${itemIdValue} from bookmark`
    : `Add ad${itemIdValue} to bookmark`;

  return (
    <BookmarkContainer
      className={additionalClasses}
      $isActive={active}
      $isWebView={isWebView}
      $isCurrentItemPage={isCurrentItemPage}
      $isGalleryHeader={isGalleryHeader}
      $renderCurrentIcons={renderCurrentIcons}
    >
      <BookmarkArea
        onClick={toggleBookmark}
        data-cy="bookmark-icon"
        data-stat={stat}
        data-action={active ? 'remove_bookmark' : 'add_bookmark'}
        role="button"
        aria-label={ariaLabel}
        $renderCurrentIcons={renderCurrentIcons}
      >
        {renderCurrentIcons && (
          <Icon
            IconComponent={active ? FavoriteFilledIcon : FavoriteIcon}
            width={22}
            height={22}
            indent={false}
          />
        )}
      </BookmarkArea>
    </BookmarkContainer>
  );
};

export const Bookmark = connect(null, mapDispatchToProps)(BookmarkComponent);
